/**
 * What each action-bar action does, on today's flows (reuse, not rebuild):
 * - Open: the views' own open (`useItemActions`): a folder at its bare path,
 *   a file through the shared entity navigation.
 * - Move: the destination picker (`CommandPalette` in parent mode, as the
 *   tree and collection pages use it) into `useBulkMove` (optimistic,
 *   all-or-nothing, one bulk move back per parent on Undo). A refusal
 *   toasts the server's reason per item.
 * - Copy and Merge: `useBulkOperations` (its copy with undo, its merge with
 *   `MergeConfirmDialog`), fed this selection.
 * - Delete: `BulkDeleteConfirmDialog` then `useBulkTrash`.
 * - Favorite, Download: `useQuickActions`.
 * - Rename: in place on `renameSurface` (`startRename`; the view draws
 *   InlineRenameField), started once a menu it was chosen from has given
 *   focus back, so that return cannot blur the new field. With no surface
 *   (the phone views), `RenameItemDialog`.
 */

import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { SelectionActionId, WorkspaceItem } from '@bower/shared';

import { useWorkspaceId } from '../../../contexts/WorkspaceContext';
import { useBulkOperations } from '../../../hooks/entity/useBulkOperations';
import { moveFailureText, useBulkMove } from '../../../hooks/workspace/useBulkMove';
import { useAppToast } from '../../../hooks/ui/useAppToast';
import { useSelectionStore, type ItemSurface } from '../../../stores/selectionStore';
import { afterMenuReturnsFocus } from '../../../utils/afterMenuReturnsFocus';
import BulkDeleteConfirmDialog from '../../dialogs/BulkDeleteConfirmDialog';
import { MergeConfirmDialog } from '../../dialogs/MergeConfirmDialog';
import { CommandPalette } from '../../features/search/CommandPalette';
import type { ParentOption } from '../../features/search/CommandPalette';
import { useItemActions } from '../views/grid/useItemActions';
import { RenameItemDialog } from './RenameItemDialog';
import { useBulkTrash } from './useBulkTrash';
import { useQuickActions } from './useQuickActions';
import { refreshFolderLists } from '../../../lib/folderListRefresh';

export function useActionBarHandlers(
  selected: WorkspaceItem[],
  renameSurface: ItemSurface | null,
  /** After Delete moved at least one item to the trash, with those items. */
  onTrashed?: (trashed: readonly WorkspaceItem[]) => void,
) {
  const workspaceId = useWorkspaceId() ?? '';
  const queryClient = useQueryClient();
  const toast = useAppToast();
  const bulkMove = useBulkMove();
  const { open } = useItemActions(selected, 'action_bar');
  const { favorite, download, isFavorite } = useQuickActions();
  const { trash, isDeleting } = useBulkTrash();
  const [dialog, setDialog] = useState<'move' | 'delete' | 'rename' | null>(null);
  const refresh = () =>
    refreshFolderLists(queryClient, { queryKey: ['workspace-items', { workspaceId }] });
  const bulkOps = useBulkOperations({
    items: selected,
    getItemId: (item) => item.id,
    onRefresh: refresh,
    entityType: 'mixed',
  });
  const ids = selected.map((item) => item.id);
  const byType = {
    projects: selected.filter((item) => item.kind === 'folder'),
    artifacts: selected.filter((item) => item.kind === 'file'),
  };
  const survivor = [...byType.artifacts].sort((a, b) => a.createdAt.localeCompare(b.createdAt))[0];

  // Never offer a moving folder itself, nor the one place every item already
  // is. A moving folder's own descendants can still show: the server refuses
  // that cycle with its reason.
  const parents = new Set(selected.map((item) => item.parentId ?? workspaceId));
  const excludeParentIds = [
    ...byType.projects.map((item) => item.id),
    ...(parents.size === 1 ? parents : []),
  ];

  const onMoveTarget = ({ type: parentType, id: parentId, name: parentName }: ParentOption) => {
    setDialog(null);
    const items = selected;
    // mutateAsync, not mutate's per-call callbacks: the optimistic update
    // takes the rows off screen, the bar unmounts, and a per-call onError on
    // an unmounted observer never fires. The promise settles regardless.
    bulkMove
      .mutateAsync({
        items,
        targetFolderId: parentType === 'workspace' ? null : parentId,
        // The same "To <name>" line a drag's toast carries.
        targetName: parentName,
      })
      .catch((error: unknown) =>
        toast({
          title: 'Could not move',
          description: moveFailureText(error, items),
          status: 'error',
        }),
      );
  };

  const run: Record<SelectionActionId, () => void> = {
    open: () => open(0),
    move: () => setDialog('move'),
    copy: () => {
      bulkOps.setSelectedIds(ids);
      bulkOps.openBulkCopy();
    },
    merge: () => {
      bulkOps.setSelectedIds(ids);
      bulkOps.onMergeDialogOpen();
    },
    favorite: () => favorite(selected),
    download: () => void download(selected),
    delete: () => setDialog('delete'),
    rename: () => {
      const id = selected[0]?.id;
      if (!renameSurface || id === undefined) return setDialog('rename');
      afterMenuReturnsFocus(() => useSelectionStore.getState().startRename(id, renameSurface));
    },
  };

  const dialogs = (
    <>
      <CommandPalette
        isOpen={dialog === 'move'}
        onClose={() => setDialog(null)}
        onSelectParentOption={onMoveTarget}
        excludeParentIds={excludeParentIds}
        placeholder="Move to..."
      />
      <CommandPalette
        isOpen={bulkOps.isBulkParentSelectorOpen}
        onClose={bulkOps.onBulkParentSelectorClose}
        onSelectParent={(type, id) => bulkOps.handleBulkMoveOrCopy(type, id, byType)}
        placeholder="Copy to..."
      />
      <MergeConfirmDialog
        isOpen={bulkOps.isMergeDialogOpen}
        onClose={bulkOps.onMergeDialogClose}
        onConfirm={bulkOps.handleBulkMerge}
        noteCount={selected.length}
        survivingNoteTitle={survivor?.name ?? 'Untitled'}
        attachmentCount={0}
        isMerging={bulkOps.isMerging}
      />
      <BulkDeleteConfirmDialog
        isOpen={dialog === 'delete'}
        onClose={() => setDialog(null)}
        onConfirm={() =>
          void trash(selected)
            .then((trashed) => {
              if (trashed.length > 0) onTrashed?.(trashed);
            })
            .finally(() => setDialog(null))
        }
        itemCount={selected.length}
        isLoading={isDeleting}
      />
      <RenameItemDialog
        item={dialog === 'rename' ? (selected[0] ?? null) : null}
        onClose={() => setDialog(null)}
        onRenamed={() => void refresh()}
      />
    </>
  );

  return { run, dialogs, isFavorite: isFavorite(selected) };
}
